import React, { useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import { getToday, getTodayM } from 'utils/date';

import GanttChartScaleDays from 'components/gantt-chart-scale-days';
import GanttChartScaleWeeks from 'components/gantt-chart-scale-weeks';
import GanttChartScaleMonths from 'components/gantt-chart-scale-months';
import GanttChartScaleYears from 'components/GanttChartScaleYears';
import GanttChartLinesDays from 'components/gantt-chart-lines-days';
import GanttChartLinesWeeks from 'components/gantt-chart-lines-weeks';
import GanttChartLinesMonths from 'components/gantt-chart-lines-months';
import GanttChartLinesYears from 'components/GanttChartLinesYears';
import GanttChartSide from 'components/GanttChartSide';
import Tooltip from 'components/tooltip';
import DraggableScrollContainer from 'components/DraggableScrollContainer';

import Placeholder from 'components/placeholders/projects-gantt.placeholder';

import { GanttChartProvider } from 'context/GanttChartContext';

import IconArrowLeft from 'icons/icon-arrow-left';
import IconArrowRight from 'icons/icon-arrow-right';

const DAYS_OFFSET = 2;
const MIN_DAYS = 45;

const VIEW_CONFIG = {
    day: {
        dayWidth: 46,
        minDays: MIN_DAYS,
        daysOffset: DAYS_OFFSET * 2
    },
    week: {
        dayWidth: 11,
        minDays: MIN_DAYS * 8,
        daysOffset: DAYS_OFFSET * 7
    },
    month: {
        dayWidth: 4,
        minDays: MIN_DAYS * 16,
        daysOffset: DAYS_OFFSET * 30
    },
    year: {
        dayWidth: 1,
        minDays: MIN_DAYS * 16 * 4,
        daysOffset: DAYS_OFFSET * 30 * 4
    },
}

const GanttChart = ({ id, viewMode, start, end, loading, scrollableClassName, sideElement, sideHead, sideFoot, sideContent, compactView, children, onShowBefore, onShowAfter, onViewModeChange, provideController, t }) => {
    const element = useRef(null);
    const scrollElement = useRef(null);
    const lastScrolledDate = useRef(null);
    const centerRef = useRef(null);

    const dayWidth = useMemo(() => {
        return VIEW_CONFIG[viewMode].dayWidth;
    }, [viewMode]);

    const computedStart = useMemo(() => {
        return moment(Math.max(Math.min(moment(start).valueOf(), getToday()), getTodayM().add(-2, 'years').valueOf()));
    }, [start]);

    const computedEnd = useMemo(() => {
        return moment(Math.min(Math.max(moment(end).valueOf(), getTodayM().add(40, 'days').valueOf()), getTodayM().add(2, 'years').valueOf()));;
    }, [end]);

    const daysOffset = useMemo(() => {
        return VIEW_CONFIG[viewMode].daysOffset;;
    }, [viewMode]);

    const minDays = useMemo(() => {
        return VIEW_CONFIG[viewMode].minDays;;
    }, [viewMode]);

    const duration = useMemo(() => {
        return Math.max(computedEnd.add(daysOffset, 'days').diff(computedStart, 'days'), minDays);
    }, [daysOffset, computedEnd, computedStart, minDays]);

    const chartStart = useMemo(() => {
        const date = moment(computedStart).clone().subtract(daysOffset, 'days');

        switch (viewMode) {
            case 'week':
                return date.startOf('week');

            case 'month':
                return date.startOf('month');

            case 'year':
                return date.startOf('year');

            default:
               return date.add(-3, 'days').startOf('day');
        }
    }, [viewMode, computedStart, daysOffset]);

    const todayPosition = useMemo(() => {
        switch (viewMode) {
            case 'week':
                return dayWidth * getTodayM().startOf('week').diff(chartStart, 'days');

            case 'month':
                return dayWidth * getTodayM().startOf('month').diff(chartStart, 'days');

            case 'year':
                return dayWidth * getTodayM().startOf('year').diff(chartStart, 'days');

            default:
                return dayWidth * getTodayM().diff(chartStart, 'days');
        }
    }, [viewMode, dayWidth, chartStart]);

    const startPosition = useMemo(() => {
        switch (viewMode) {
            case 'week':
                return dayWidth * moment(start).add(-3, 'days').startOf('week').diff(chartStart, 'days');

            case 'month':
                return dayWidth * moment(start).add(-3, 'days').startOf('month').diff(chartStart, 'days');

            case 'year':
                return dayWidth * moment(start).add(-3, 'days').startOf('year').diff(chartStart, 'days');

            default:
                return dayWidth * moment(start).add(-1, 'days').diff(chartStart, 'days');
        }
    }, [start, viewMode, dayWidth, chartStart]);

    const getMainSize = useCallback(() => {
        return (scrollElement.current?.clientWidth || 0) - (scrollElement.current?.querySelector('.gantt-chart-side')?.clientWidth || 0);
    }, [scrollElement]);

    const center = useCallback(useStart => {
        if (!scrollElement.current) {
            return;
        }

        let position;

        if (lastScrolledDate.current && !useStart) {
            position = dayWidth * moment(lastScrolledDate.current).diff(chartStart, 'days')
        } else {
            if (useStart) {
                position = startPosition;
            } else {
                position = todayPosition - dayWidth * daysOffset * 2;
            }
        }

        scrollElement.current.scrollLeft = Math.max(position, 0);
    }, [scrollElement, startPosition, todayPosition, dayWidth, daysOffset, chartStart]);

    centerRef.current = center;

    const zoomToFit = useCallback(() => {
        const totalDays = (end - start) / 8.64e+7;
        const availableWidth = getMainSize();

        const viewId = Object.keys(VIEW_CONFIG).find(viewId => {
            const view = VIEW_CONFIG[viewId];

            return availableWidth / view.dayWidth > totalDays;
        }) || 'year';

        onViewModeChange(viewId);

        requestAnimationFrame(() => {
            centerRef.current(true);
        });
    }, [start, end, onViewModeChange]);

    const chartDuration = useMemo(() => {
        return (duration + daysOffset * 2) || 0;
    }, [duration, daysOffset])

    const scaleComponent = useMemo(() => {
        const props = {
            chartDuration,
            chartStart,
            scaleItemWidth: dayWidth
        };

        switch (viewMode) {
            case 'week':
                return <GanttChartScaleWeeks {...props} />;

            case 'month':
                return <GanttChartScaleMonths {...props} />;

            case 'year':
                return <GanttChartScaleYears {...props} />;

            default:
                return <GanttChartScaleDays {...props} chartStart={chartStart.toDate()} />;
        }
    }, [viewMode, chartDuration, chartStart, dayWidth])

    const linesComponent = useMemo(() => {
        const props = {
            chartDuration,
            chartStart,
            scaleItemWidth: dayWidth
        };

        switch (viewMode) {
            case 'week':
                return <GanttChartLinesWeeks {...props} />;

            case 'month':
                return <GanttChartLinesMonths {...props} />;

            case 'year':
                return <GanttChartLinesYears {...props} />;

            default:
                return <GanttChartLinesDays {...props} />;
        }
    }, [viewMode, chartDuration, chartStart, dayWidth])

    const todayComponent = useMemo(() => {
        let width = 0;

        switch (viewMode) {
            case 'week':
                width = dayWidth * 7;
                break;

            case 'month':
                width = dayWidth * getTodayM().daysInMonth();
                break;

            case 'year':
                width = dayWidth * (365 + getTodayM().isLeapYear() ? 1 : 0);
                break;

            default:
                width = dayWidth;
        }

        return <div className="gantt-chart-today-highlight" style={{left: `${todayPosition}px`, width: `${width}px`}} />;
    }, [viewMode, dayWidth, todayPosition]);

    const chartStyle = useMemo(() => ({
        minWidth: `${chartDuration * dayWidth}px`
    }), [chartDuration, dayWidth]);

    const handleScroll = () => {
        if (!scrollElement.current) {
            return;
        }

        const daysScrolled = Math.round(scrollElement.current?.scrollLeft / dayWidth);

        lastScrolledDate.current = chartStart.clone().add(daysScrolled, 'days').valueOf();
    }

    useLayoutEffect(() => {
        requestAnimationFrame(() => {
            center();
        });
    }, [scrollElement]);

    useEffect(() => {
        center();
    }, [center, chartStyle]);

    useEffect(() => {
        const ctrl = {
            zoomToFit
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        }
    }, [zoomToFit, provideController]);

    if (loading) {
        return <Placeholder />;
    }

    return (
        <div className={'gantt-chart' + (compactView ? ' gantt-chart-compact' : '')}>
            <DraggableScrollContainer className={'gantt-chart-scrollable nice-scroll ' + scrollableClassName} onScroll={handleScroll} innerRef={scrollElement}>
                <div className="gantt-chart-scrollable-inner">
                    {
                        sideElement ? sideElement : (
                            <GanttChartSide
                                id={id}
                                head={sideHead}
                                foot={sideFoot}
                            >{ sideContent }</GanttChartSide>
                        )
                    }

                    <div className="gantt-chart-inner" style={chartStyle}>
                        <div className="gantt-chart-head">
                            { scaleComponent }
                        </div>

                        <div className="gantt-chart-body">
                            { todayComponent }

                            { linesComponent }

                            <GanttChartProvider chartStart={chartStart.toDate()} dayWidth={dayWidth}>
                                { children }
                            </GanttChartProvider>
                        </div>
                    </div>

                    {
                        onShowBefore ? (
                            <Tooltip tooltip={ t('Show Older') }>
                                <button className="btn gantt-chart-more-arrow gantt-chart-more-arrow-before" onClick={onShowBefore}>
                                    <IconArrowLeft />
                                </button>
                            </Tooltip>
                        ) : null
                    }

                    {
                        onShowAfter ? (
                            <Tooltip tooltip={ t('Show Newer') }>
                                <button className="btn gantt-chart-more-arrow gantt-chart-more-arrow-after" onClick={onShowAfter}>
                                    <IconArrowRight />
                                </button>
                            </Tooltip>
                        ) : null
                    }
                </div>
            </DraggableScrollContainer>
        </div>
    );
}

GanttChart.defaultProps = {
    viewMode: 'day',
    start: getTodayM().add(-10, 'days').valueOf(),
    end: getTodayM().add(10, 'days').valueOf(),
    scrollableClassName: ''
};

export default withTranslation('date')(GanttChart);
